/**
 * reCAPTCHA v3 (silent) token minting for the sign-in gate.
 *
 * The site key is public and safe to ship in the bundle. The matching
 * secret lives only in the sibling infra/agents projects, where the
 * Cognito `DefineAuthChallenge` trigger verifies the token server-side
 * and fails closed (see docs/auth.md §13). The token is minted on the
 * sign-in page only, at submit time, and carried to the trigger via
 * `clientMetadata.recaptchaToken`.
 *
 * v3 tokens are single-use and expire in ~2 minutes, so we mint at the
 * moment of submit rather than on page load.
 */

import { authConfig } from '../config/auth-config';

const SITE_KEY = authConfig.recaptchaSiteKey;
const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;

// Hard ceiling on how long a single mint may take. The whole chain
// (script load → grecaptcha.ready → grecaptcha.execute) can stall with no
// resolve AND no reject — e.g. Firefox-for-Android's Enhanced Tracking
// Protection lets the api.js script load but blocks the anchor frame it
// needs, so `ready()`/`execute()` simply never call back. Without this
// guard the caller's `await` hangs forever and the sign-in button stays
// stuck on "Signing in…" with no error. We instead reject with
// RecaptchaError so the UI shows the bot-detection message and re-enables.
// Exported so the sign-in form can size its countdown progress bar to the
// same window — the bar filling up coincides with this timeout firing.
//
// Set generously (30s) so slow mobile networks that would eventually mint a
// valid token aren't cut off prematurely; the auth forms pair this with a
// "Try again" button (and getRecaptchaToken self-heals its script cache on
// failure) so a genuine stall is recoverable without a full page reload.
// Overridable via VITE_RECAPTCHA_MINT_TIMEOUT_MS (e2e sets a small value so
// the stall-recovery test doesn't have to wait the full 30s); a missing or
// invalid value falls back to 30s.
export const MINT_TIMEOUT_MS = readMintTimeoutMs();

function readMintTimeoutMs(): number {
  const raw = import.meta.env.VITE_RECAPTCHA_MINT_TIMEOUT_MS;
  const n = raw === undefined || raw === '' ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 30_000;
}

/**
 * Structured reason for a client-side mint failure. Forwarded to the
 * server-side telemetry ingest (see recaptchaReport.ts) so a failure that
 * never reaches Cognito — no token was minted, so DefineAuthChallenge never
 * runs — is still recorded in CloudWatch alongside server-side rejections.
 */
export type RecaptchaFailureReason =
  | 'not_configured'
  | 'load_failed'
  | 'timeout'
  | 'init_failed'
  | 'exec_failed'
  | 'empty_token';

/** Thrown when a token can't be minted (script blocked, key missing, …). */
export class RecaptchaError extends Error {
  readonly reason: RecaptchaFailureReason;
  /**
   * Optional extra detail — e.g. the stringified argument grecaptcha's
   * `execute()` reject callback passed. Never contains PII (no email /
   * password / token), so it's safe to forward to the telemetry ingest.
   */
  readonly detail?: string;
  constructor(reason: RecaptchaFailureReason, message: string, detail?: string) {
    super(message);
    this.name = 'RecaptchaError';
    this.reason = reason;
    this.detail = detail;
  }
}

/** Narrow an unknown error to a RecaptchaError (client-side mint failure). */
export function isRecaptchaError(err: unknown): err is RecaptchaError {
  return err instanceof RecaptchaError;
}

interface Grecaptcha {
  ready(cb: () => void): void;
  execute(siteKey: string, opts: { action: string }): Promise<string>;
}

function getGrecaptcha(): Grecaptcha | undefined {
  return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
}

// Module-level so the script is injected at most once per page load.
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (!SITE_KEY) {
      reject(
        new RecaptchaError('not_configured', 'reCAPTCHA site key is not configured'),
      );
      return;
    }
    // Already present (e.g. a prior preload injected it) — reuse it.
    if (getGrecaptcha()) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the cached promise so a later call can retry from scratch.
      scriptPromise = null;
      reject(new RecaptchaError('load_failed', 'Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Warm the reCAPTCHA script ahead of submit so `getRecaptchaToken` is
 * fast when the user clicks. Safe to call on mount; failures are
 * swallowed here and surfaced properly at mint time.
 */
export function preloadRecaptcha(): void {
  void loadScript().catch(() => {
    /* surfaced by getRecaptchaToken */
  });
}

/**
 * Reject `p` with a RecaptchaError if it hasn't settled within `ms`. Used
 * to convert a silently-stalled reCAPTCHA (no resolve, no reject) into a
 * surfaced error the sign-in form can recover from. The timer is cleared
 * the moment `p` settles, so a fast mint never pays for it.
 */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new RecaptchaError('timeout', 'reCAPTCHA timed out'));
    }, ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(typeof err === 'string' ? err : JSON.stringify(err)));
      },
    );
  });
}

/**
 * Mint a fresh, single-use v3 token for `action` (e.g. 'login'). Throws
 * `RecaptchaError` if the script is unavailable, execution fails, or the
 * whole chain stalls past `MINT_TIMEOUT_MS`. Call this at submit time,
 * immediately before the protected request.
 */
export async function getRecaptchaToken(action: string): Promise<string> {
  if (!SITE_KEY) {
    throw new RecaptchaError('not_configured', 'reCAPTCHA site key is not configured');
  }
  try {
    return await withTimeout(mintToken(action), MINT_TIMEOUT_MS);
  } catch (err) {
    // Drop the cached script promise so an explicit retry (the auth forms'
    // "Try again" button) re-injects the script and re-attempts from scratch
    // rather than re-awaiting a load/anchor-frame that already stalled. On a
    // hard block the retry still fails, but a transient stall (slow network,
    // CDN hiccup) recovers without a full page reload.
    scriptPromise = null;
    throw err;
  }
}

async function mintToken(action: string): Promise<string> {
  await loadScript();
  const grecaptcha = getGrecaptcha();
  if (!grecaptcha) {
    throw new RecaptchaError('init_failed', 'reCAPTCHA failed to initialise');
  }
  return new Promise<string>((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(SITE_KEY, { action }).then(
        (token) => {
          if (token) {
            resolve(token);
          } else {
            reject(
              new RecaptchaError('empty_token', 'reCAPTCHA returned an empty token'),
            );
          }
        },
        // grecaptcha's v3 reject usually passes no argument, but capture
        // whatever it hands us (stringified, message only) so the server
        // report carries any detail there is.
        (execErr: unknown) =>
          reject(
            new RecaptchaError(
              'exec_failed',
              'reCAPTCHA execution failed',
              execErr === undefined || execErr === null
                ? undefined
                : execErr instanceof Error
                  ? execErr.message
                  : JSON.stringify(execErr),
            ),
          ),
      );
    });
  });
}
